import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const NEW_OWNER_VALUE = '__new_owner__';

const clean = (value) => String(value || '').trim();

const AgentModal = ({ agent, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [ownerMode, setOwnerMode] = useState(agent?.owner ? 'existing' : 'existing');
  const [form, setForm] = useState({
    username: agent?.username || '',
    owner: agent?.owner || '',
    owner_region: agent?.owner_region || '',
    notes: agent?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { data: ownersData, isLoading: ownersLoading } = useQuery(
    ['agentOwners'],
    () => api.get('/agents/owners').then((res) => res.data),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const ownerOptions = useMemo(() => {
    const rows = Array.isArray(ownersData?.owners) ? ownersData.owners : [];
    const seen = new Set();
    const options = [];

    for (const row of rows) {
      const name = clean(row.owner);
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({
        owner: name,
        total: row.total || 0,
        regions: Array.isArray(row.regions) ? row.regions.filter(Boolean) : [],
      });
    }

    const currentOwner = clean(form.owner);
    if (currentOwner && ownerMode !== 'new' && !seen.has(currentOwner.toLowerCase())) {
      options.unshift({ owner: currentOwner, total: 0, regions: [] });
    }

    return options;
  }, [ownersData, form.owner, ownerMode]);

  const handleOwnerSelect = (value) => {
    if (value === NEW_OWNER_VALUE) {
      setOwnerMode('new');
      setForm((prev) => ({ ...prev, owner: '' }));
      return;
    }

    setOwnerMode('existing');
    setForm((prev) => ({ ...prev, owner: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = clean(form.username);
    const owner = clean(form.owner);
    const ownerRegion = clean(form.owner_region);

    if (!username) {
      setError('Username is required');
      return;
    }

    if (!owner) {
      setError('Owner is required');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      username,
      owner,
      owner_region: ownerRegion || null,
      notes: form.notes || null,
    };

    try {
      if (agent) {
        await api.put(`/agents/${agent.id}`, payload);
      } else {
        await api.post('/agents', payload);
      }
      queryClient.invalidateQueries('agentOwners');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6">
        <h2 className="mb-2 text-xl font-semibold">{agent ? 'Edit friendly agent' : 'Add friendly agent'}</h2>
        <p className="mb-4 text-sm text-gray-500">
          Stage 1: friendly Telegram agents. Username + owner are enough; backend resolves user_id and profile data.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Telegram username *</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="@username"
              disabled={!!agent}
            />
            <p className="mt-1 text-xs text-gray-500">Only public Telegram usernames can be auto-resolved.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Owner *</label>
            <select
              value={ownerMode === 'new' ? NEW_OWNER_VALUE : form.owner}
              onChange={(e) => handleOwnerSelect(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
              disabled={ownersLoading}
            >
              <option value="">Choose owner...</option>
              {ownerOptions.map((option) => (
                <option key={option.owner} value={option.owner}>
                  {option.owner}{option.total ? ` (${option.total})` : ''}
                  {option.regions.length ? ` — ${option.regions.join(', ')}` : ''}
                </option>
              ))}
              <option value={NEW_OWNER_VALUE}>+ Add new owner</option>
            </select>
            {ownerMode === 'new' && (
              <input
                type="text"
                value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Owner name, e.g. Люба"
                autoFocus
              />
            )}
            <p className="mt-1 text-xs text-gray-500">Pick an existing operator or add a new one.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Owner region / note</label>
            <input
              type="text"
              value={form.owner_region || ''}
              onChange={(e) => setForm({ ...form, owner_region: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Optional, e.g. Турция"
            />
            <p className="mt-1 text-xs text-gray-500">Use for cases like “Люба / Турция” while keeping owner = Люба.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Optional internal note"
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : agent ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentModal;
